import { useCallback, useEffect, useState } from "react";

import { getThemeBySlug } from "@/api/themes.api";
import { ApiRequestError } from "@/types/auth";
import type { ThemeDetailResponse } from "@/types/themes";

export interface UseThemeResult {
  readonly theme: ThemeDetailResponse | null;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly notFound: boolean;
  refetch(): void;
}

/**
 * CR-111: detail-page hook — fetches a theme by slug via the
 * `/api/themes/by-slug/{slug}` endpoint added in Phase 1.
 *
 * A 404 (ApiRequestError with status 404) is promoted to a
 * dedicated `notFound` flag so the page can render a 404 card
 * instead of a generic error banner. Any other error falls into
 * `error` unchanged.
 *
 * Passing `slug=null` short-circuits the effect — the detail route
 * renders the NotFound surface via its own guard and never needs
 * to trigger a fetch.
 */
export function useTheme(slug: string | null): UseThemeResult {
  const [theme, setTheme] = useState<ThemeDetailResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(slug !== null);
  const [error, setError] = useState<Error | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!slug) {
      setTheme(null);
      setLoading(false);
      setError(null);
      setNotFound(false);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setNotFound(false);

    getThemeBySlug(slug, ctrl.signal)
      .then((resp) => {
        if (cancelled || ctrl.signal.aborted) return;
        setTheme(resp);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled || ctrl.signal.aborted) return;
        setTheme(null);
        setLoading(false);
        if (err instanceof ApiRequestError && err.status === 404) {
          setNotFound(true);
          setError(null);
          return;
        }
        setError(err);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [slug, attempt]);

  const refetch = useCallback(() => {
    setAttempt((a) => a + 1);
  }, []);

  return { theme, loading, error, notFound, refetch };
}
